import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useGetArchiveBusinessFile } from '../../../services/loanService';
import { ArchiveHeader, LoanStatusPill, formatRand } from './archiveShared';
import { formatDocumentDate } from '../../../utils/documentDates';

/** A borrower's loan folders, one per loan reference so two facilities are never confused. */
const ArchiveLoanFolders: React.FC = () => {
  const { customerId = '' } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetArchiveBusinessFile(customerId, { query: { enabled: !!customerId } });
  const business = data?.business;
  const loans = business?.loans || [];

  if (isLoading) {
    return <div className="text-center py-5"><div className="spinner-border text-success" role="status"></div></div>;
  }
  if (isError || !business) {
    return <div className="container-fluid py-5 text-center text-muted">This borrower's file could not be loaded. <Link to="/documents">Back to all loan files</Link></div>;
  }

  return (
    <>
      <ArchiveHeader
        crumbs={[{ label: 'All loan files', to: '/documents' }, { label: business.name || 'Borrower', to: `/documents/files/${customerId}` }, { label: 'Loan-specific documents' }]}
        backTo={`/documents/files/${customerId}`}
        title="Loan-specific documents"
        subtitle="Each facility keeps its own application, assessment, agreements, disbursement and repayment evidence."
      />
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
      <div className="container-fluid">
        <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
          <div className="card-body border-bottom">
            <h5 className="fw-bold mb-0">Loan folders</h5>
            <small className="text-muted">Folder names use the LMS loan reference so two facilities can never be confused.</small>
          </div>
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead className="small text-muted">
                <tr><th className="ps-4">Loan folder</th><th>Facility</th><th>Status</th><th>Documents</th><th></th></tr>
              </thead>
              <tbody>
                {loans.map(loan => (
                  <tr key={loan.loanApplicationId} role="button" onClick={() => navigate(`/documents/loans/${loan.loanApplicationId}`)}>
                    <td className="ps-4">
                      <div className="d-flex align-items-center gap-3">
                        <div className="rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                          <i className="fa fa-folder-o fs-5"></i>
                        </div>
                        <div>
                          <div className="fw-semibold">Loan · {loan.accountNumber}</div>
                          <small className="text-muted">Disbursed {formatDocumentDate(loan.disbursementDate)}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>{loan.product || '-'}</div>
                      <small className="text-muted">{formatRand(loan.amount)}</small>
                    </td>
                    <td>
                      <LoanStatusPill status={loan.status} />
                      {loan.closedDate && <div className="small text-muted mt-1">Settled {formatDocumentDate(loan.closedDate)}</div>}
                    </td>
                    <td>
                      <div className="fw-semibold">{loan.documentCount} documents</div>
                      {loan.attentionCount
                        ? <small className="text-warning-emphasis">{loan.attentionCount} require attention</small>
                        : <small className="text-success">Complete</small>}
                    </td>
                    <td className="text-end pe-4">
                      <button className="btn btn-sm btn-outline-secondary" onClick={e => { e.stopPropagation(); navigate(`/documents/loans/${loan.loanApplicationId}`); }}>
                        <i className="fa fa-folder-open-o me-1"></i>Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default ArchiveLoanFolders;
